import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getOrgStructureSyncStatus,
  listConfigCells,
  listConfigTables,
  type ConfigTablesQuery,
  listOrgStructure,
  syncOrgStructure,
} from "@/api/configuration-server";
import { stageKeys } from "@/hooks/useConfigServerStages";

const keys = {
  tables: (projectId: string, query: ConfigTablesQuery) =>
    ["config-server", "tables", projectId, query] as const,
  cells: (projectId: string) => ["config-server", "cells", projectId] as const,
  orgStructure: (projectId: string, artifactId?: string) =>
    ["config-server", "org-structure", projectId, artifactId ?? null] as const,
  orgStructureSync: (projectId: string) =>
    ["config-server", "org-structure-sync", projectId] as const,
};

/** The page loads its bounded result set once and filters it in memory. */
export function useConfigCells(projectId: string | undefined) {
  return useQuery({
    queryKey: keys.cells(projectId ?? ""),
    queryFn: () => listConfigCells(projectId!),
    enabled: !!projectId,
    staleTime: 60_000,
  });
}

/** One page of extracted tables; filtering and paging happen on the server. */
export function useConfigTables(
  projectId: string | undefined,
  query: ConfigTablesQuery = {},
) {
  return useQuery({
    queryKey: keys.tables(projectId ?? "", query),
    queryFn: () => listConfigTables(projectId!, query),
    enabled: !!projectId,
    staleTime: 60_000,
    placeholderData: (previous) => previous,
  });
}

export function useOrgStructure(
  projectId: string | undefined,
  artifactId?: string,
  enabled = true,
) {
  return useQuery({
    queryKey: keys.orgStructure(projectId ?? "", artifactId),
    queryFn: () => listOrgStructure(projectId!, artifactId),
    enabled: !!projectId && enabled,
    staleTime: 60_000,
  });
}

export function useOrgStructureSyncStatus(projectId?: string) {
  return useQuery({
    queryKey: keys.orgStructureSync(projectId ?? ""),
    queryFn: () => getOrgStructureSyncStatus(projectId!),
    enabled: !!projectId,
    staleTime: 30_000,
  });
}

export function useSyncOrgStructure(projectId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["config-server", "org-structure-sync", projectId],
    mutationFn: () => syncOrgStructure(projectId!),
    onSettled: () => {
      if (!projectId) return;
      // Recarrega as leituras do Gerenciamento e o status da sincronização.
      for (const queryKey of [
        ["config-server", "org-structure", projectId],
        ["config-server", "tables", projectId],
        keys.cells(projectId),
        ["sscui-activity-sscui-tables", projectId],
        ["sscui-activity-sscui-records", projectId],
        ["sscui-activity-module_config-tables", projectId],
        ["sscui-activity-module_config-records", projectId],
        keys.orgStructureSync(projectId),
        stageKeys.all(projectId),
        ["migration-items", projectId],
        ["pipeline", projectId],
      ]) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}
